import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, unref as _unref, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "w-max-content" }
const _hoisted_2 = { class: "w-max-content" }
const _hoisted_3 = {
  key: 1,
  class: "tab-history"
}
const _hoisted_4 = { class: "header" }
const _hoisted_5 = { class: "flex-1" }
const _hoisted_6 = { class: "flex-1" }
const _hoisted_7 = { class: "flex-1" }
const _hoisted_8 = { class: "content" }
const _hoisted_9 = { class: "w-max-content" }
const _hoisted_10 = { class: "w-max-content" }
const _hoisted_11 = {
  key: 2,
  class: "tab-charts"
}
const _hoisted_12 = { class: "header" }
const _hoisted_13 = { class: "flex-1" }
const _hoisted_14 = { class: "flex-1" }
const _hoisted_15 = { class: "content" }
const _hoisted_16 = { class: "flex flex-col" }

import { computed, ref, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import store from "@/store";
import moment from "moment";

import { toast } from "@/common/utils/toast";
import { maskCurrency } from "@/common/utils/mask";

import {
  invoiceList,
  invoiceDel,
  invoiceChart,
} from "@/common/api/rest/invoice";

import type { InvoiceType } from "@/common/types/invoice";

import { PanelTheme } from "@/common/themes";

import {
  baseConfirm,
  baseDate,
  baseSelect,
  baseModal,
  baseTable,
  tabMenu,
} from "@/common/components";

import { ChartBar } from "@/components";

type TabActiveType = "PENDING" | "HISTORY" | "REPORT" | "CHARTS";


export default /*@__PURE__*/_defineComponent({
  __name: 'index',
  setup(__props) {

const { t, tm } = useI18n();

const route = useRoute();

const tabList = computed(() => [
  {
    key: "PENDING",
    label: t("invoice.labels.pending").toUpperCase(),
  },
  {
    key: "HISTORY",
    label: t("invoice.labels.history").toUpperCase(),
  },
  //   {
  //     key: "REPORT",
  //     label: t("common.labels.report").toUpperCase(),
  //   },
  {
    key: "CHARTS",
    label: t("common.labels.charts").toUpperCase(),
  },
]);

const tableHeader = computed(() => [
  { key: "date", label: t("invoice.labels.date") },
  { key: "member", label: t("invoice.labels.member") },
  { key: "product_type", label: t("invoice.labels.product") },
  { key: "product", label: t("invoice.labels.name") },
  { key: "price", label: t("invoice.labels.value") },
  { key: "status", label: t("invoice.labels.status") },
]);

const typeOptions = computed(
  () => tm("invoice.enum.product_type") as Record<string, string>
);

const statusEnum = computed(
  () => tm("invoice.enum.status") as Record<string, string>
);

const currencyEnum = computed(
  () => tm("invoice.enum.currency") as Record<string, string>
);

const statusOptions = computed(() => [
  {
    option: t("invoice.labels.all"),
    value: "ALL",
  },
  {
    option: t("invoice.labels.pending"),
    value: "PENDING",
  },
  {
    option: t("invoice.labels.paid"),
    value: "PAID",
  },
  {
    option: t("invoice.labels.canceled"),
    value: "CANCELLED",
  },
  {
    option: t("invoice.labels.refunded"),
    value: "REFUNDED",
  },
]);

const monthsOptions = computed(() => {
  const months = Object.entries(
    tm("date.months.full") as Record<string, string>
  ).map(([_, option], index) => ({
    option,
    value: index + 1,
  }));

  return [
    {
      option: t("invoice.labels.all"),
      value: "all",
    },
    ...months,
  ];
});

const monthsList = computed(() =>
  Object.values(tm("date.months.full") as Record<string, string>)
);

const yearOptions = computed(() => {
  const currentYear = new Date().getFullYear();
  const yearsBefore = Array.from({ length: 11 }, (_, index) => index)
    .filter((_, index) => index > 0)
    .map((index) => currentYear - index)
    .sort((a, b) => a - b);

  const yearsAfter = Array.from(
    { length: 11 },
    (_, index) => currentYear + index
  );

  return yearsBefore.concat(yearsAfter).map((year) => ({
    option: `${year}`,
    value: `${year}`,
  }));
});

const tabActive = ref<TabActiveType>("PENDING");

const tableData = ref<InvoiceType[]>([]);
const selected = ref<InvoiceType | null>(null);
const selectedStatus = ref<string>("PENDING");

const historyStartDate = ref("");
const historyEndDate = ref("");

const chartData = ref([]);
const chartLabel = ref([]);
const chartMonth = ref("all");
const chartYear = ref();
const chartKey = ref(0);

const limit = ref<number>(5);
const page = ref<number>(0);
const pages = ref<number>(0);
const search = ref<string>("");
const searched = ref<number>(0);
const total = ref<number>(0);

const loading = ref(false);
const showConfirmDel = ref(false);

function fetchData() {
  loading.value = true;

  invoiceList({
    search: search.value,
    page: page.value,
    limit: limit.value,
    status: selectedStatus.value,
    start_date: historyStartDate.value,
    end_date: historyEndDate.value,
    order: "DESC",
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        tableData.value = data.list;
        page.value = data.infos.page;
        pages.value = data.infos.pages;
        total.value = data.infos.total;
        searched.value = data.infos.count;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      loading.value = false;
    });
}

function searchItem(input: string) {
  search.value = input;
  fetchData();
}

function changePage(value: number) {
  page.value = value;
  fetchData();
}

function selectTab(tab: TabActiveType) {
  if (tabActive.value === tab) return;

  tabActive.value = tab;

  if (tab === "PENDING") {
    selectedStatus.value = "PENDING";
    historyStartDate.value = "";
    historyEndDate.value = "";
    fetchData();
  }

  if (tab === "HISTORY") {
    historyStartDate.value = moment().subtract(30, "days").format("YYYY-MM-DD");
    historyEndDate.value = moment().format("YYYY-MM-DD");
    selectedStatus.value = "ALL";
    fetchData();
  }

  if (tab === "CHARTS") {
    chartYear.value = String(moment().year());
    fetchChartData();
  }
}

function remove(data: InvoiceType) {
  selected.value = data;
  showConfirmDel.value = true;
}

function removeCancel() {
  selected.value = null;
  showConfirmDel.value = false;
}

function removeConfirm() {
  if (!selected.value) return;

  showConfirmDel.value = false;
  store.commit("SET_LOADING", true);

  invoiceDel(selected.value.invoice_id)
    .then((response) => {
      const { success, message } = response;
      fetchData();
      removeCancel();
      toast({ message: message, type: success ? "success" : "error" });
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function changeStatusType() {
  fetchData();
}

function changeHistoryDate() {
  fetchData();
}

function showInvoice(data: InvoiceType) {
  window.open(data.invoice_url, "_blank");
}

// CHARTS
function resumeChart(data: any) {
  chartLabel.value = [];
  chartData.value = [];

  if (data?.length === 0) {
    chartKey.value++;
    return;
  }

  const labels = [];
  const datasets = [
    {
      label: "Pendente",
      data: [],
      backgroundColor: "#a1a1a1",
    },
    {
      label: "Pago",
      data: [],
      backgroundColor: "#003b23",
    },
    {
      label: "Cancelado",
      data: [],
      backgroundColor: "#df9800",
    },
    {
      label: "Extornado",
      data: [],
      backgroundColor: "#fd1a1e",
    },
  ];

  Object.entries(data).forEach(([key, value]) => {
    const month = Number(key) - 1;

    const infos = (value as any).infos;
    const pendingAmount = infos?.pending?.amount;
    const paidAmount = infos?.paid?.amount;
    const canceledAmount = infos?.canceled?.amount;
    const refundedAmount = infos?.refunded?.amount;

    labels.push(monthsList.value[month]);

    datasets[0].data.push(pendingAmount);
    datasets[1].data.push(paidAmount);
    datasets[2].data.push(canceledAmount);
    datasets[3].data.push(refundedAmount);
  });

  chartLabel.value = labels;
  chartData.value = datasets;
  chartKey.value++;
}

function fetchChartData() {
  invoiceChart(chartMonth.value, chartYear.value)
    .then((response) => {
      const { success, data, message } = response;
      if (success) {
        resumeChart(data.list);
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

onMounted(() => {
  fetchData();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(PanelTheme), null, {
    default: _withCtx(() => [
      _createVNode(_unref(tabMenu), {
        tabs: tabList.value,
        active: tabActive.value,
        tooltip: false,
        width: "fit-content",
        "padding-inline": "1.5rem",
        "onSelect:tab": selectTab
      }, {
        content: _withCtx(() => [
          (tabActive.value === 'PENDING')
            ? (_openBlock(), _createBlock(_unref(baseTable), {
                key: 0,
                header: tableHeader.value,
                data: tableData.value,
                limit: limit.value,
                page: page.value,
                pages: pages.value,
                total: total.value,
                searched: searched.value,
                loading: loading.value,
                options: true,
                view: true,
                del: true,
                edit: false,
                add: false,
                "onChange:page": changePage,
                onSearching: searchItem,
                "onBtn:view": showInvoice,
                "onBtn:del": remove
              }, {
                status: _withCtx(({ data }) => [
                  _createTextVNode(_toDisplayString(statusEnum.value[data.status]), 1)
                ]),
                product: _withCtx(({ data }) => [
                  _createElementVNode("div", _hoisted_1, _toDisplayString(data.product?.name), 1)
                ]),
                date: _withCtx(({ data }) => [
                  _createElementVNode("div", _hoisted_2, _toDisplayString(_unref(moment)(data.created).format("DD/MM/YYYY")), 1)
                ]),
                price: _withCtx(({ data }) => [
                  _createTextVNode(_toDisplayString(currencyEnum.value[data.currency]) + " " + _toDisplayString(_unref(maskCurrency)(data.price, data.currency)), 1)
                ]),
                product_type: _withCtx(({ data }) => [
                  _createTextVNode(_toDisplayString(typeOptions.value[data.product_type]), 1)
                ]),
                member: _withCtx(({ data }) => [
                  _createTextVNode(_toDisplayString(data.member?.first_name), 1)
                ]),
                _: 1
              }, 8, ["header", "data", "limit", "page", "pages", "total", "searched", "loading"]))
            : _createCommentVNode("", true),
          (tabActive.value === 'HISTORY')
            ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                _createElementVNode("div", _hoisted_4, [
                  _createElementVNode("div", _hoisted_5, [
                    _createVNode(_unref(baseSelect), {
                      modelValue: selectedStatus.value,
                      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectedStatus).value = $event)),
                      value: selectedStatus.value,
                      options: statusOptions.value,
                      "is-empty": false,
                      onChange: _cache[1] || (_cache[1] = ($event: any) => (changeStatusType()))
                    }, {
                      label: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_unref(t)("invoice.labels.type")), 1)
                      ]),
                      _: 1
                    }, 8, ["modelValue", "value", "options"])
                  ]),
                  _createElementVNode("div", _hoisted_6, [
                    _createVNode(_unref(baseDate), {
                      modelValue: historyStartDate.value,
                      "onUpdate:modelValue": [
                        _cache[2] || (_cache[2] = ($event: any) => ((historyStartDate).value = $event)),
                        _cache[3] || (_cache[3] = ($event: any) => (changeHistoryDate()))
                      ],
                      value: historyStartDate.value,
                      "value-mask": "YYYY-MM-DD",
                      placeholder: "dd/mm/yyyy"
                    }, {
                      label: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_unref(t)("invoice.labels.start_date")) + " * ", 1)
                      ]),
                      _: 1
                    }, 8, ["modelValue", "value"])
                  ]),
                  _createElementVNode("div", _hoisted_7, [
                    _createVNode(_unref(baseDate), {
                      modelValue: historyEndDate.value,
                      "onUpdate:modelValue": [
                        _cache[4] || (_cache[4] = ($event: any) => ((historyEndDate).value = $event)),
                        _cache[5] || (_cache[5] = ($event: any) => (changeHistoryDate()))
                      ],
                      value: historyEndDate.value,
                      "value-mask": "YYYY-MM-DD",
                      placeholder: "dd/mm/yyyy"
                    }, {
                      label: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_unref(t)("invoice.labels.end_date")) + " * ", 1)
                      ]),
                      _: 1
                    }, 8, ["modelValue", "value"])
                  ])
                ]),
                _createElementVNode("div", _hoisted_8, [
                  _createVNode(_unref(baseTable), {
                    header: tableHeader.value,
                    data: tableData.value,
                    limit: limit.value,
                    page: page.value,
                    pages: pages.value,
                    total: total.value,
                    searched: searched.value,
                    loading: loading.value,
                    options: true,
                    view: true,
                    del: true,
                    edit: false,
                    add: false,
                    "onChange:page": changePage,
                    onSearching: searchItem,
                    "onBtn:view": showInvoice,
                    "onBtn:del": remove
                  }, {
                    status: _withCtx(({ data }) => [
                      _createTextVNode(_toDisplayString(statusEnum.value[data.status]), 1)
                    ]),
                    product: _withCtx(({ data }) => [
                      _createElementVNode("div", _hoisted_9, _toDisplayString(data.product?.name), 1)
                    ]),
                    date: _withCtx(({ data }) => [
                      _createElementVNode("div", _hoisted_10, _toDisplayString(_unref(moment)(data.created).format("DD/MM/YYYY")), 1)
                    ]),
                    price: _withCtx(({ data }) => [
                      _createTextVNode(_toDisplayString(currencyEnum.value[data.currency]) + " " + _toDisplayString(_unref(maskCurrency)(data.price, data.currency)), 1)
                    ]),
                    product_type: _withCtx(({ data }) => [
                      _createTextVNode(_toDisplayString(typeOptions.value[data.product_type]), 1)
                    ]),
                    member: _withCtx(({ data }) => [
                      _createTextVNode(_toDisplayString(data.member?.first_name), 1)
                    ]),
                    _: 1
                  }, 8, ["header", "data", "limit", "page", "pages", "total", "searched", "loading"])
                ])
              ]))
            : _createCommentVNode("", true),
          (tabActive.value === 'CHARTS')
            ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                _createElementVNode("div", _hoisted_12, [
                  _createElementVNode("div", _hoisted_13, [
                    _createVNode(_unref(baseSelect), {
                      modelValue: chartMonth.value,
                      "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((chartMonth).value = $event)),
                      value: chartMonth.value,
                      options: monthsOptions.value,
                      "is-empty": false,
                      onChange: _cache[7] || (_cache[7] = ($event: any) => (fetchChartData()))
                    }, {
                      label: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_unref(t)("date.labels.month")), 1)
                      ]),
                      _: 1
                    }, 8, ["modelValue", "value", "options"])
                  ]),
                  _createElementVNode("div", _hoisted_14, [
                    _createVNode(_unref(baseSelect), {
                      modelValue: chartYear.value,
                      "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((chartYear).value = $event)),
                      value: chartYear.value,
                      options: yearOptions.value,
                      "is-empty": false,
                      onChange: _cache[9] || (_cache[9] = ($event: any) => (fetchChartData()))
                    }, {
                      label: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_unref(t)("date.labels.year")), 1)
                      ]),
                      _: 1
                    }, 8, ["modelValue", "value", "options"])
                  ])
                ]),
                _createElementVNode("div", _hoisted_15, [
                  _createElementVNode("div", _hoisted_16, [
                    _cache[10] || (_cache[10] = _createElementVNode("p", null, "MOVIMENTAÇÃO FINANCEIRA", -1)),
                    (_openBlock(), _createBlock(_unref(ChartBar), {
                      key: chartKey.value,
                      labels: chartLabel.value,
                      data: chartData.value
                    }, null, 8, ["labels", "data"]))
                  ])
                ])
              ]))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }, 8, ["tabs", "active"]),
      _createVNode(_unref(baseConfirm), {
        show: showConfirmDel.value,
        title: 
        _unref(t)('common.labels.remove', {
          concat: _unref(t)('common.labels.finance'),
        }).toUpperCase()
      ,
        message: 
        _unref(t)('common.texts.remove_message', {
          concat: selected.value?.product?.name,
        })
      ,
        onCancel: removeCancel,
        onConfirm: removeConfirm
      }, null, 8, ["show", "title", "message"])
    ]),
    _: 1
  }))
}
}

})